/**
 * Sales types ("trades") — the company's job categories, as configured in
 * Admin → Sales types and served by GET /api/v1/sales-types.
 *
 * Until 2026-09-22 this was a fixed enum of six with brand colours baked
 * into the CSS palette. It's now data: each type carries its own hex colour
 * and the Spire values that map to it. To keep the 15 call sites small, the
 * old function names survive but take the type list as their first
 * argument (`useSalesTypes()` provides it):
 *
 *   const types = useSalesTypes();
 *   const trade = normalizeTrade(types, order.customer.userDef2);   // key | 'unknown'
 *   tradeLabel(types, trade)      // "Install"
 *   tradeColor(types, trade)      // "#7F77DD" (the type's own colour)
 *   tradeTint(types, trade)       // { bg, fg, border } for pills / cards
 *
 * A `Trade` is now a sales-type KEY (string) or 'unknown'. Keys of the six
 * seeded types are unchanged ('install', 'service', 'pm', 'sales',
 * 'emergency', 'warranty'), so tech skills stored before the switch still
 * resolve.
 */

import { useQuery } from '@tanstack/react-query';
import { api, type SalesType } from './api.js';

export type Trade = string;
export const UNKNOWN_TRADE: Trade = 'unknown';

export const SALES_TYPES_KEY = ['sales-types'] as const;

/**
 * The six the app shipped with — the fallback until the list loads, and
 * what an environment without the sales_types table effectively has.
 * Mirrors BUILT_IN_SALES_TYPES in the API.
 */
export const BUILT_IN_SALES_TYPES: ReadonlyArray<SalesType> = [
  { key: 'service', label: 'Service', color: '#185FA5', matchValues: ['service'], isSkill: true, active: true, sortOrder: 0 },
  { key: 'install', label: 'Install', color: '#7F77DD', matchValues: ['install', 'installation'], isSkill: true, active: true, sortOrder: 1 },
  { key: 'pm', label: 'PM', color: '#1D9E75', matchValues: ['pm', 'preventive', 'maintenance'], isSkill: true, active: true, sortOrder: 2 },
  { key: 'sales', label: 'Sales', color: '#EF9F27', matchValues: ['sales'], isSkill: true, active: true, sortOrder: 3 },
  { key: 'emergency', label: 'Emergency', color: '#E24B4A', matchValues: ['emergency', 'after hours', 'after-hours'], isSkill: true, active: true, sortOrder: 4 },
  { key: 'warranty', label: 'Warranty', color: '#94A3B8', matchValues: ['warranty'], isSkill: false, active: true, sortOrder: 5 },
];

/** Always returns a usable list — the built-ins until the fetch lands or if it fails. */
export function useSalesTypes(): ReadonlyArray<SalesType> {
  const query = useQuery({
    queryKey: SALES_TYPES_KEY,
    queryFn: ({ signal }) => api.salesTypes.list(signal),
    staleTime: 5 * 60_000,
  });
  return query.data ?? BUILT_IN_SALES_TYPES;
}

export function findSalesType(types: ReadonlyArray<SalesType>, key: Trade): SalesType | undefined {
  return types.find((t) => t.key === key);
}

/** Active types, in display order — what pickers and filters show. */
export function activeSalesTypes(types: ReadonlyArray<SalesType>): SalesType[] {
  return types.filter((t) => t.active).sort((a, b) => a.sortOrder - b.sortOrder);
}

/**
 * Map a Spire value (`SALES TYP`, customer userDef2) to a sales-type key.
 * Exact match on a type's match value wins; otherwise the first type with a
 * match value the Spire value STARTS WITH; otherwise 'unknown'. Case- and
 * whitespace-insensitive. Inactive types still match so historical orders
 * keep their label, but they sort last so an active type wins a tie.
 */
/**
 * Trade for an ORDER: the order's own type UDF wins (field-created jobs
 * write the chosen sales type there — every Enercare job shares one
 * bill-to, so the customer's userDef2 can't tell them apart), falling back
 * to the customer's userDef2 for office-created orders.
 */
export function orderTrade(
  types: ReadonlyArray<SalesType>,
  order: { udf?: Record<string, string> | null; customer: { userDef2: string | null } },
  typeUdfKey: string,
): Trade {
  const own = order.udf?.[typeUdfKey]?.trim();
  return normalizeTrade(types, own || order.customer.userDef2);
}

export function normalizeTrade(types: ReadonlyArray<SalesType>, raw: string | null | undefined): Trade {
  if (!raw) return UNKNOWN_TRADE;
  const v = raw.trim().toLowerCase();
  if (v === '') return UNKNOWN_TRADE;
  const ordered = [...types].sort((a, b) => Number(b.active) - Number(a.active) || a.sortOrder - b.sortOrder);
  for (const t of ordered) {
    if (t.matchValues.some((m) => m === v)) return t.key;
  }
  for (const t of ordered) {
    if (t.matchValues.some((m) => v.startsWith(m) || (m.length >= 4 && v.includes(m)))) return t.key;
  }
  return UNKNOWN_TRADE;
}

/** Label for chips. 'unknown' → 'Job'; a key that no longer exists → the key itself. */
export function tradeLabel(types: ReadonlyArray<SalesType>, trade: Trade): string {
  if (trade === UNKNOWN_TRADE) return 'Job';
  return findSalesType(types, trade)?.label ?? trade;
}

/** The type's own hex colour; neutral grey for unknown / retired keys. */
export function tradeColor(types: ReadonlyArray<SalesType>, trade: Trade): string {
  return findSalesType(types, trade)?.color ?? NEUTRAL_COLOR;
}

export const NEUTRAL_COLOR = '#94A3B8';

export interface TradeTint {
  /** Soft background — the colour at low alpha, so it works on light and dark surfaces. */
  bg: string;
  /** Readable text on that background — the colour darkened (light mode) via color-mix. */
  fg: string;
  /** Slightly stronger than bg, for card borders. */
  border: string;
  /** The raw colour, for dots / stripes. */
  solid: string;
}

/**
 * Derive pill / card tints from a hex colour. Uses CSS `color-mix` so the
 * same rule works in both themes: the background is the colour blended
 * into the page surface, the text is the colour blended toward the ink.
 */
export function tintFor(hex: string): TradeTint {
  return {
    bg: `color-mix(in srgb, ${hex} 16%, var(--color-background-primary, #ffffff))`,
    fg: `color-mix(in srgb, ${hex} 70%, var(--color-text-primary, #0F172A))`,
    border: `color-mix(in srgb, ${hex} 40%, var(--color-background-primary, #ffffff))`,
    solid: hex,
  };
}

export function tradeTint(types: ReadonlyArray<SalesType>, trade: Trade): TradeTint {
  return tintFor(tradeColor(types, trade));
}

/**
 * Trades a tech can hold as skills — active types flagged isSkill. Keys of
 * retired types a tech still carries are shown greyed by the callers.
 */
export function skillTypes(types: ReadonlyArray<SalesType>): SalesType[] {
  return activeSalesTypes(types).filter((t) => t.isSkill);
}
